import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ShieldCheck,
    Truck,
    CalendarDays,
    MapPin,
    Star,
    ArrowRight,
    MousePointerClick,
    CircleDollarSign,
    Heart,
    Compass,
    Sparkles,
} from "lucide-react";

const HERO_IMG =
    "https://images.unsplash.com/photo-1556894546-d227c2d4df3b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxvcmxhbmRvJTIwcGFsbSUyMHRyZWVzfGVufDB8fHx8MTc4NTgxNzUzN3ww&ixlib=rb-4.1.0&q=85";
const VAN_IMG =
    "https://images.unsplash.com/photo-1641199788912-9a7385a35c82?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHx3aGl0ZSUyMGRlbGl2ZXJ5JTIwdmFufGVufDB8fHx8MTc4NTgxNzU0Nnww&ixlib=rb-4.1.0&q=85";
const COUPLE_IMG =
    "https://images.unsplash.com/photo-1508963493744-76fce69379c0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxoYXBweSUyMHNlbmlvciUyMGNvdXBsZSUyMHZhY2F0aW9ufGVufDB8fHx8MTc4NTgxNzUzOHww&ixlib=rb-4.1.0&q=85";

const stagger = {
    hidden: {},
    show: { transition: { staggerChildren: 0.08 } },
};
const rise = {
    hidden: { opacity: 0, y: 16 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

export default function Home() {
    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src={HERO_IMG}
                        alt="Orlando palm trees against a bright blue sky"
                        className="h-full w-full object-cover"
                    />
                    {/* Base darken so text is always legible regardless of image */}
                    <div className="absolute inset-0 bg-[color:var(--brand-navy)]/70" />
                    {/* Left-heavy vignette for the copy column */}
                    <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--brand-navy)] via-[color:var(--brand-navy)]/70 to-transparent" />
                    {/* Subtle bottom tone to seat the CTAs */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--brand-navy)]/50 to-transparent" />
                </div>

                <div className="relative mx-auto grid max-w-7xl gap-10 px-4 pb-24 pt-20 sm:px-6 lg:grid-cols-12 lg:gap-12 lg:px-8 lg:pt-32">
                    <motion.div
                        initial="hidden"
                        animate="show"
                        variants={stagger}
                        className="lg:col-span-7"
                    >
                        <motion.span
                            variants={rise}
                            className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-[color:var(--brand-gold)] backdrop-blur"
                        >
                            <Star className="h-3.5 w-3.5 fill-current" />
                            Central Florida&apos;s Trusted Rental
                        </motion.span>
                        <motion.h1
                            variants={rise}
                            className="mt-6 max-w-3xl font-display text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl"
                        >
                            Go Further. Do More.
                            <br />
                            <span className="text-[color:var(--brand-gold)]">
                                Freedom Delivered.
                            </span>
                        </motion.h1>
                        <motion.p
                            variants={rise}
                            className="mt-6 max-w-xl text-base font-medium leading-relaxed text-white/95 sm:text-lg"
                            style={{
                                textShadow: "0 2px 12px rgba(10,37,64,0.55)",
                            }}
                        >
                            Premium mobility scooter rentals delivered
                            throughout Central Florida for vacations, cruises,
                            business travel, injury recovery, and everyday
                            mobility needs.
                        </motion.p>
                        <motion.div
                            variants={rise}
                            className="mt-8 flex flex-wrap items-center gap-4"
                        >
                            <Link
                                to="/scooters/pride-victory-10"
                                data-testid="hero-book-scooter-button"
                                className="hero-cta pill-btn inline-flex items-center gap-2 text-white"
                            >
                                Book a Scooter
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                            <Link
                                to="/delivery-area"
                                data-testid="hero-delivery-link"
                                className="pill-btn inline-flex items-center gap-2 border border-white/40 bg-white/10 text-white backdrop-blur hover:bg-white/20"
                            >
                                See Delivery Areas
                            </Link>
                        </motion.div>

                        <motion.div
                            variants={rise}
                            className="mt-10 flex flex-wrap gap-6 text-sm font-medium text-white/95"
                            style={{
                                textShadow: "0 2px 10px rgba(10,37,64,0.5)",
                            }}
                        >
                            <span className="inline-flex items-center gap-2">
                                <ShieldCheck className="h-4 w-4 text-[color:var(--brand-green)]" />
                                Sanitized between rentals
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <Truck className="h-4 w-4 text-[color:var(--brand-green)]" />
                                Free within 15 mi of Orlando
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <CalendarDays className="h-4 w-4 text-[color:var(--brand-green)]" />
                                Daily • Weekly • Monthly
                            </span>
                        </motion.div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.35, duration: 0.6 }}
                        className="relative lg:col-span-5"
                    >
                        <div className="card-surface relative overflow-hidden p-6 shadow-xl sm:p-8">
                            <div className="mb-1 text-xs font-semibold uppercase tracking-widest text-[color:var(--brand-green)]">
                                Fastest Way to Book
                            </div>
                            <h3 className="font-display text-2xl font-extrabold text-[color:var(--brand-navy)]">
                                Reserve your scooter in 60 seconds
                            </h3>
                            <ol className="mt-6 space-y-4 text-sm text-slate-600">
                                {[
                                    "Pick your rental dates",
                                    "Choose delivery or pickup",
                                    "Pay securely & we deliver",
                                ].map((step, i) => (
                                    <li
                                        key={step}
                                        className="flex items-start gap-3"
                                    >
                                        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[color:var(--brand-navy)] font-display text-sm font-bold text-white">
                                            {i + 1}
                                        </span>
                                        <span className="pt-0.5 text-slate-700">
                                            {step}
                                        </span>
                                    </li>
                                ))}
                            </ol>
                            <Link
                                to="/scooters/pride-victory-10"
                                data-testid="hero-widget-cta"
                                className="hero-cta pill-btn mt-8 inline-flex w-full items-center justify-center text-white"
                            >
                                Start Booking
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* WHY US — refined benefits */}
            <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
                <div className="mb-16 max-w-3xl">
                    <div className="text-xs font-semibold uppercase tracking-widest text-[color:var(--brand-green)]">
                        Why Choose Us
                    </div>
                    <h2 className="mt-3 font-display text-4xl font-extrabold leading-tight tracking-tight text-[color:var(--brand-navy)] sm:text-5xl">
                        Why choose Central Florida Mobility Rentals?
                    </h2>
                    <p className="mt-4 font-display text-xl font-medium text-[color:var(--brand-gold)] sm:text-2xl">
                        More than a rental. Peace of mind delivered.
                    </p>
                    <p className="mt-6 text-base leading-relaxed text-slate-600 sm:text-lg">
                        When mobility becomes a concern, the rental process
                        shouldn&apos;t become another one. Our job is simple —
                        take that off your list. Whether you&apos;re travelling
                        for a cruise, in town for a convention, visiting
                        family, or recovering from surgery, we make temporary
                        mobility easy, dependable, and worry-free.
                    </p>
                </div>

                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {[
                        {
                            icon: MousePointerClick,
                            title: "Easy online booking",
                            body: "Reserve in minutes with a simple, secure checkout. Pick your dates, choose delivery, done.",
                        },
                        {
                            icon: ShieldCheck,
                            title: "Dependable, start to finish",
                            body: "Every rental is professionally prepared, delivered on time, and supported the entire way through.",
                        },
                        {
                            icon: CircleDollarSign,
                            title: "Honest pricing",
                            body: "Transparent rates with no hidden fees. You'll know exactly what you're paying before you check out.",
                        },
                        {
                            icon: Heart,
                            title: "Local service that cares",
                            body: "Locally owned and operated. When you reach out, you speak with real people — never a call center.",
                        },
                        {
                            icon: Compass,
                            title: "Mobility for every journey",
                            body: "Vacations, cruises, business travel, conventions, family visits, injury recovery — whatever brings you here, we're ready.",
                        },
                        {
                            icon: Sparkles,
                            title: "Confidence included",
                            body: "You're not just renting a scooter. You're gaining the confidence to enjoy your plans without worrying about mobility.",
                        },
                    ].map((f) => (
                        <div
                            key={f.title}
                            data-testid={`why-us-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            className="card-surface p-8"
                        >
                            <span className="grid h-11 w-11 place-items-center rounded-xl bg-[color:var(--brand-navy)]/5 text-[color:var(--brand-navy)]">
                                <f.icon className="h-5 w-5 text-[color:var(--brand-green)]" />
                            </span>
                            <h3 className="mt-5 font-display text-xl font-extrabold tracking-tight text-[color:var(--brand-navy)]">
                                {f.title}
                            </h3>
                            <p className="mt-3 text-[15px] leading-relaxed text-slate-600">
                                {f.body}
                            </p>
                        </div>
                    ))}
                </div>

                <p className="mt-16 text-center font-display text-2xl font-medium tracking-tight text-[color:var(--brand-navy)] sm:text-3xl">
                    We make mobility one less thing to worry about.
                </p>
            </section>

            {/* DELIVERY AREAS */}
            <section className="bg-white">
                <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                    <div className="mb-12 flex flex-wrap items-end justify-between gap-4">
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-widest text-[color:var(--brand-green)]">
                                Where We Deliver
                            </div>
                            <h2 className="mt-2 font-display text-4xl font-extrabold leading-tight tracking-tight text-[color:var(--brand-navy)] sm:text-5xl">
                                Serving Central Florida
                            </h2>
                        </div>
                        <Link
                            to="/delivery-area"
                            data-testid="home-delivery-area-link"
                            className="text-sm font-semibold text-[color:var(--brand-navy)] hover:text-[color:var(--brand-green)]"
                        >
                            View full delivery area →
                        </Link>
                    </div>
                    <div className="grid gap-6 md:grid-cols-3">
                        {[
                            {
                                city: "Orlando",
                                desc: "Hotels near the Orange County Convention Center, ICON Park, Universal, Disney Springs, and downtown.",
                            },
                            {
                                city: "Kissimmee",
                                desc: "Vacation homes and resorts along 192 near Disney World and Celebration — FREE delivery included.",
                            },
                            {
                                city: "Port Canaveral",
                                desc: "Cruise terminal delivery for Carnival, Disney, Royal Caribbean & MSC.",
                            },
                        ].map((c) => (
                            <div
                                key={c.city}
                                className="card-surface p-8"
                                data-testid={`home-area-${c.city.toLowerCase().replace(/\s/g, "-")}`}
                            >
                                <MapPin className="h-5 w-5 text-[color:var(--brand-gold)]" />
                                <h3 className="mt-4 font-display text-2xl font-extrabold text-[color:var(--brand-navy)]">
                                    {c.city}
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-slate-600">
                                    {c.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <div
                    className="relative overflow-hidden rounded-3xl bg-[color:var(--brand-navy)] p-10 sm:p-14"
                    data-testid="home-cta-banner"
                >
                    <div className="grain absolute inset-0" />
                    <div className="relative flex flex-col items-start gap-6 lg:flex-row lg:items-center lg:justify-between">
                    <div className="max-w-2xl">
                        <div className="text-xs font-semibold uppercase tracking-widest text-[color:var(--brand-gold)]">
                            Ready when you are
                        </div>
                        <h3 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                            One less thing to worry about.
                        </h3>
                        <p className="mt-3 text-slate-300">
                            Reserve your Pride Victory 10 in minutes — vacation,
                            convention, cruise, or recovery. We&apos;ll take
                            care of the rest.
                        </p>
                    </div>
                        <Link
                            to="/scooters/pride-victory-10"
                            data-testid="home-cta-book-button"
                            className="hero-cta pill-btn inline-flex items-center gap-2 text-white"
                        >
                            Book the Pride Victory 10
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
